import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, NavLink, useLocation, useNavigate } from 'react-router-dom';
import './App.css';

import Dashboard  from './pages/Dashboard';
import Customers  from './pages/Customers';
import Sales      from './pages/Sales';
import Projects   from './pages/Projects';
import Inventory  from './pages/Inventory';
import Production from './pages/Production';
import Reports    from './pages/Reports';
import ShopFloor     from './pages/ShopFloor';
import PinPadScreen   from './pages/PinPad';

const BREAKPOINT = 768;

function Sidebar({ isOpen, onNavClick }) {
  return (
    <div className={`sidebar${isOpen ? '' : ' sidebar--collapsed'}`}>
      <div className="sidebar-logo">
        <h1>CGS</h1>
        <span>ERP System</span>
      </div>
      <nav className="sidebar-nav">
        <NavLink to="/" end className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')} onClick={onNavClick}>
          <span className="nav-icon">▦</span> Dashboard
        </NavLink>
        <NavLink to="/customers" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')} onClick={onNavClick}>
          <span className="nav-icon">◈</span> Customers
        </NavLink>
        <NavLink to="/sales" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')} onClick={onNavClick}>
          <span className="nav-icon">◎</span> Sales
        </NavLink>
        <NavLink to="/projects" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')} onClick={onNavClick}>
          <span className="nav-icon">⬡</span> Projects
        </NavLink>
        <NavLink to="/inventory" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')} onClick={onNavClick}>
          <span className="nav-icon">▣</span> Inventory
        </NavLink>
        <NavLink to="/production" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')} onClick={onNavClick}>
          <span className="nav-icon">⚙</span> Production
        </NavLink>
        <NavLink to="/reports" className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')} onClick={onNavClick}>
          <span className="nav-icon">≡</span> Reports
        </NavLink>
      </nav>
      <div className="sidebar-footer">
        Creative Glass Systems<br />
        v1.0.0
      </div>
    </div>
  );
}

function HamburgerBtn({ isOpen, onClick }) {
  return (
    <button className={`hamburger-btn${isOpen ? ' hamburger-btn--open' : ''}`} onClick={onClick}
      aria-label={isOpen ? 'Close menu' : 'Open menu'}>
      <span className="ham-bar" />
      <span className="ham-bar" />
      <span className="ham-bar" />
    </button>
  );
}

// ─── Single PIN gate: management PIN → /, shopfloor PIN → /shopfloor ──────────
function AppInner() {
  const navigate = useNavigate();
  const location = useLocation();

  // Auth state — cleared on refresh (no persistence by design)
  const [authed, setAuthed] = useState(false);

  // Sidebar state (always initialised — hooks must not be conditional)
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= BREAKPOINT);
  const [isMobile,    setIsMobile]    = useState(() => window.innerWidth < BREAKPOINT);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < BREAKPOINT;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggle     = useCallback(() => setSidebarOpen(s => !s), []);
  const onNavClick = useCallback(() => { if (window.innerWidth < BREAKPOINT) setSidebarOpen(false); }, []);

  // PIN gate — shown until authenticated
  const handleAuth = useCallback((access) => {
    setAuthed(true);
    const dest = access === 'management' ? '/' : '/shopfloor';
    // Only navigate if we're not already at the right place
    if (!location.pathname.startsWith(access === 'management' ? '/' : '/shopfloor') ||
        (access === 'shopfloor' && !location.pathname.startsWith('/shopfloor'))) {
      navigate(dest, { replace: true });
    }
  }, [navigate, location.pathname]);

  if (!authed) {
    return <PinPadScreen onAuth={handleAuth} />;
  }

  // ── Shop floor layout (no sidebar) ─────────────────────────────────────────
  if (location.pathname.startsWith('/shopfloor')) {
    return (
      <Routes>
        <Route path="/shopfloor/*" element={<ShopFloor />} />
      </Routes>
    );
  }

  // ── Main ERP layout (sidebar) ───────────────────────────────────────────────
  const mainMargin = sidebarOpen && !isMobile ? 220 : 0;
  return (
    <div className="app-layout">
      {sidebarOpen && isMobile && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}
      <Sidebar isOpen={sidebarOpen} onNavClick={onNavClick} />
      <main className="main-content" style={{ marginLeft: mainMargin }}>
        <div className="topbar">
          <HamburgerBtn isOpen={sidebarOpen} onClick={toggle} />
          {!sidebarOpen && <span className="topbar-logo">CGS <span>ERP</span></span>}
        </div>
        <div className="page-body">
          <Routes>
            <Route path="/"           element={<Dashboard />} />
            <Route path="/customers"  element={<Customers />} />
            <Route path="/sales"      element={<Sales />} />
            <Route path="/projects"   element={<Projects />} />
            <Route path="/inventory"  element={<Inventory />} />
            <Route path="/production" element={<Production />} />
            <Route path="/reports"    element={<Reports />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}
